import { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/solid";

const DAYS_SHORT = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const formatMonthLabel = (date) =>
  date.toLocaleDateString("en-US", { month: "long", year: "numeric" });

const buildCalendarCells = (currentMonth) => {
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const days = [];

  for (let i = 0; i < firstDay.getDay(); i++) {
    const prevDate = new Date(year, month, -firstDay.getDay() + i + 1);
    days.push({ date: prevDate, isCurrentMonth: false });
  }

  for (let day = 1; day <= lastDay.getDate(); day++) {
    days.push({ date: new Date(year, month, day), isCurrentMonth: true });
  }

  const remainingCells = 42 - days.length;
  for (let day = 1; day <= remainingCells; day++) {
    days.push({ date: new Date(year, month + 1, day), isCurrentMonth: false });
  }

  return days;
};

const isSameDay = (dateA, dateB) =>
  !!dateA && !!dateB && dateA.toDateString() === dateB.toDateString();

const isToday = (date) => isSameDay(date, new Date());

const deriveTimeValues = (time) => {
  if (!time) {
    return { hour: "12", minute: "00", ampm: "AM" };
  }
  let hour = time.getHours();
  const minute = time.getMinutes().toString().padStart(2, "0");
  const ampm = hour >= 12 ? "PM" : "AM";

  if (hour === 0) hour = 12;
  else if (hour > 12) hour -= 12;

  return {
    hour: hour.toString().padStart(2, "0"),
    minute,
    ampm,
  };
};

const clampNumber = (value, min, max) => Math.min(max, Math.max(min, value));
const convertTo24Hour = (hour12, isPm) => isPm ? (hour12 === 12 ? 12 : hour12 + 12) : (hour12 === 12 ? 0 : hour12);

/**
 * DateTimePopup component - Common date and time picker popup
 * @param {Object} props
 * @param {Date|null} props.selectedDate - Currently selected date
 * @param {Date|null} props.selectedTime - Currently selected time
 * @param {function} props.onDateSelect - Callback when date is selected
 * @param {function} props.onTimeChange - Callback when time changes
 * @param {function} props.onBook - Callback when Book button is clicked
 * @param {React.RefObject} props.wrapperRef - Ref to the input wrapper element for positioning
 * @param {boolean} props.open - Whether the popup is open
 */
const DateTimePopup = ({
  selectedDate,
  selectedTime,
  onDateSelect,
  onTimeChange,
  onBook,
  wrapperRef,
  open,
}) => {
  const [activeTab, setActiveTab] = useState("date");
  const [currentMonth, setCurrentMonth] = useState(selectedDate || new Date());
  const [position, setPosition] = useState(null);
  const [localHour, setLocalHour] = useState(null);

  useEffect(() => {
    if (selectedDate) {
      setCurrentMonth(selectedDate);
    }
  }, [selectedDate]);

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }

    if (!wrapperRef.current) return;

    const updatePosition = () => {
      if (!wrapperRef.current) return;
      
      const rect = wrapperRef.current.getBoundingClientRect();
      const popupWidth = 420;
      const popupHeight = 400;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Calculate left position - align to left edge of input
      let leftPos = rect.left;
      
      // If popup would overflow on the right, shift left
      if (leftPos + popupWidth > viewportWidth) {
        leftPos = Math.max(16, viewportWidth - popupWidth - 16);
      }
      
      // Ensure it doesn't go off screen to the left
      if (leftPos < 16) {
        leftPos = 16;
      }

      // Calculate top position - position below input with 4px gap
      let topPos = rect.bottom + 4;
      
      // If not enough space below, position above
      if (rect.bottom + popupHeight + 4 > viewportHeight) {
        topPos = Math.max(4, rect.top - popupHeight - 4);
      }
      
      // Ensure it doesn't go off screen at top
      if (topPos < 4) {
        topPos = 4;
      }

      setPosition({ left: leftPos, top: topPos });
    };

    updatePosition();

    // Update on scroll and resize
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open, wrapperRef]);

  const navigateMonth = (direction) => {
    setCurrentMonth((prev) => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + direction);
      return newMonth;
    });
  };

  const handleDateSelect = (date) => {
    onDateSelect(date);
    setActiveTab("time");
  };

  const calendarCells = useMemo(
    () => buildCalendarCells(currentMonth),
    [currentMonth]
  );

  const timeValues = useMemo(
    () => deriveTimeValues(selectedTime),
    [selectedTime]
  );

  const handleHourChange = useCallback((rawValue) => {
    if (!rawValue || rawValue === "") {
      setLocalHour("00");
      return;
    }

    const cleaned = rawValue.replace(/^0+/, "") || "0";
    const parsed = parseInt(cleaned, 10);
    
    if (isNaN(parsed) || parsed > 12) return;
    
    const clamped = clampNumber(parsed, 0, 12);
    setLocalHour(clamped.toString());

    const hour12 = clamped === 0 ? 12 : clamped;
    const baseTime = new Date(selectedTime || new Date());
    const currentTime = deriveTimeValues(baseTime);
    baseTime.setHours(convertTo24Hour(hour12, currentTime.ampm === "PM"));
    onTimeChange(baseTime);
  }, [selectedTime, onTimeChange]);

  const handleMinuteChange = useCallback((rawValue) => {
    const baseTime = new Date(selectedTime || new Date());
    baseTime.setMinutes(clampNumber(parseInt(rawValue, 10) || 0, 0, 59));
    onTimeChange(baseTime);
  }, [selectedTime, onTimeChange]);

  const handleAmpmChange = useCallback((ampm) => {
    const baseTime = new Date(selectedTime || new Date());
    const currentTime = deriveTimeValues(baseTime);
    baseTime.setHours(convertTo24Hour(parseInt(currentTime.hour, 10), ampm === "PM"));
    onTimeChange(baseTime);
  }, [selectedTime, onTimeChange]);

  const handleTimeChange = useCallback((type, rawValue) => {
    if (type === "hour") handleHourChange(rawValue);
    else if (type === "minute") handleMinuteChange(rawValue);
    else if (type === "ampm") handleAmpmChange(rawValue);
  }, [handleHourChange, handleMinuteChange, handleAmpmChange]);

  const handleHourBlur = useCallback(() => {
    if (!localHour || localHour === "" || localHour === "00") {
      setLocalHour("00");
      return;
    }
    const parsed = parseInt(localHour, 10);
    setLocalHour(isNaN(parsed) ? "00" : clampNumber(parsed, 0, 12).toString().padStart(2, "0"));
  }, [localHour]);

  if (!open || !wrapperRef.current || !position) {
    return null;
  }

  const popupContent = (
    <div
      className="hc-datetime-popup bg-white border border-gray-200 rounded-lg shadow-lg"
      style={{
        position: "fixed",
        left: `${position.left}px`,
        top: `${position.top}px`,
        width: "420px",
        maxHeight: "400px",
        overflowY: "auto",
        zIndex: 9999,
      }}
    >
      <div className="p-4">
        {/* Tab Navigation */}
        <div className="flex bg-gray-100 rounded-lg p-1 mb-4">
          <button
            className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
              activeTab === "date"
                ? "bg-REELTeal text-white shadow-sm"
                : "text-gray-600 hover:text-gray-800"
            }`}
            onClick={() => setActiveTab("date")}
          >
            Date
          </button>
          <button
            className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
              activeTab === "time"
                ? "bg-REELTeal text-white shadow-sm"
                : "text-gray-600 hover:text-gray-800"
            }`}
            onClick={() => setActiveTab("time")}
          >
            Time
          </button>
        </div>
        {/* Date Picker Panel */}
        {activeTab === "date" && (
          <div>
            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => navigateMonth(-1)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <ChevronLeftIcon className="h-5 w-5 text-gray-600" />
              </button>
              <h3 className="text-sm font-medium text-gray-700">
                {formatMonthLabel(currentMonth)}
              </h3>
              <button
                onClick={() => navigateMonth(1)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <ChevronRightIcon className="h-5 w-5 text-gray-600" />
              </button>
            </div>
            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {DAYS_SHORT.map((day) => (
                <div
                  key={day}
                  className="text-xs font-small text-gray-500 text-center"
                >
                  {day}
                </div>
              ))}
              {calendarCells.map((dayObj, index) => {
                const { date, isCurrentMonth } = dayObj;
                const isSelected = isSameDay(date, selectedDate);
                const isTodayDate = isToday(date);
                return (
                  <button
                    key={index}
                    onClick={() => isCurrentMonth && handleDateSelect(date)}
                    className={`
                      ml-4 h-6 w-6 text-sm rounded-md transition-colors
                      ${
                        !isCurrentMonth
                          ? "text-gray-300 cursor-not-allowed"
                          : "text-gray-700 hover:bg-gray-100"
                      }
                      ${
                        isSelected
                          ? "bg-REELTeal text-white hover:bg-REELTeal"
                          : ""
                      }
                      ${
                        isTodayDate && !isSelected
                          ? "bg-blue-100 text-blue-600 font-medium"
                          : ""
                      }
                    `}
                    disabled={!isCurrentMonth}
                  >
                    {date.getDate()}
                  </button>
                );
              })}
            </div>
            <hr />
            {/* Today Button */}
            <div className="text-center mt-1">
              <button
                onClick={() => handleDateSelect(new Date())}
                className="text-md text-REELTeal hover:text-teal-700 font-medium"
              >
                Today
              </button>
            </div>
          </div>
        )}
        {/* Time Picker Panel */}
        {activeTab === "time" && (
          <div>
            {/* Selected Date Display */}
            {selectedDate && (
              <div className="text-center mb-6 font-medium text-gray-700">
                {selectedDate.toLocaleDateString('en-US', { 
                  month: 'long', 
                  day: 'numeric', 
                  year: 'numeric' 
                })}
              </div>
            )}
            {selectedDate ? (
              <>
                {/* Time Input */}
                <div className="flex justify-center items-start space-x-4 mb-6">
                  {/* Hour */}
                  <div className="flex flex-col items-center">
                    <input
                      type="number"
                      value={localHour ?? timeValues.hour ?? "00"}
                      onChange={(e) => handleTimeChange("hour", e.target.value)}
                      onFocus={(e) => (e.target.value === "0" || e.target.value === "00") && e.target.select()}
                      onBlur={handleHourBlur}
                      min="1"
                      max="12"
                      className="w-20 h-16 text-2xl text-center border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    />
                    <span className="text-xs text-gray-500 mt-1">Hour</span>
                  </div>
                  {/* Colon */}
                  <div className="text-2xl font-bold text-gray-400 pt-3">
                    :
                  </div>
                  {/* Minute */}
                  <div className="flex flex-col items-center">
                    <input
                      type="number"
                      value={timeValues.minute}
                      onChange={(e) => handleTimeChange("minute", e.target.value)}
                      min="0"
                      max="59"
                      className="w-20 h-16 text-2xl text-center border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    />
                    <span className="text-xs text-gray-500 mt-1">
                      Minute
                    </span>
                  </div>
                  {/* AM/PM Toggle */}
                  <div className="flex flex-col border border-gray-300 rounded-lg overflow-hidden h-16 w-16 ml-2 self-start">
                    <button
                      className={`flex-1 text-sm font-medium transition-colors ${
                        timeValues.ampm === "AM"
                          ? "bg-REELTeal text-white"
                          : "text-gray-600 hover:bg-gray-50"
                      }`}
                      onClick={() => handleTimeChange("ampm", "AM")}
                    >
                      AM
                    </button>
                    <div className="h-px bg-gray-300" />
                    <button
                      className={`flex-1 text-sm font-medium transition-colors ${
                        timeValues.ampm === "PM"
                          ? "bg-REELTeal text-white"
                          : "text-gray-600 hover:bg-gray-50"
                      }`}
                      onClick={() => handleTimeChange("ampm", "PM")}
                    >
                      PM
                    </button>
                  </div>
                </div>
                {/* Divider */}
                <hr className="my-6 border-gray-200" />
                {/* Book Button */}
                <div className="flex justify-end">
                  <button
                    className="bg-REELTeal hover:bg-REELTeal text-white font-medium py-2 px-6 rounded-full flex items-center gap-2 transition-colors"
                    onClick={onBook}
                  >
                    Book
                    <ChevronRightIcon className="h-4 w-4" />
                  </button>
                </div>
              </>
            ) : (
              <div className="text-center text-gray-400 py-8">
                Please select a date first
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  // Use portal to render directly to body, avoiding parent container CSS issues
  if (typeof document !== 'undefined') {
    return createPortal(popupContent, document.body);
  }
  return popupContent;
};

export default DateTimePopup;